import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { api, BACKEND_URL, errMsg } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { RoleBadge } from "@/components/RoleBadge";
import { useAuth } from "@/context/AuthContext";

const ago = (iso) => formatDistanceToNow(new Date(iso), { addSuffix: true });

const AuthorLine = ({ a, at, small }) => {
  const name = <span className={`font-medium ${small ? "text-xs" : "text-sm"}`}>{a.name || a.username}</span>;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar user={a} className={small ? "h-7 w-7" : "h-10 w-10"} />
      <div className="min-w-0">
        <p className="flex items-center gap-2 truncate">{a.role === "tech" ? <Link to={`/tech/${a.username}`} className="hover:text-pink-600">{name}</Link> : name}<RoleBadge role={a.role} /></p>
        <p className="truncate font-mono text-[11px] text-stone-500">@{a.username} · {ago(at)}</p>
      </div>
    </div>
  );
};

const full = (src) => (src.startsWith("/") ? `${BACKEND_URL}${src}` : src);

const Images = ({ images }) => {
  if (!images.length) return null;
  const one = images.length === 1;
  return (
    <div data-testid="post-images" className={`mt-4 grid gap-1 overflow-hidden rounded-2xl ${one ? "" : "grid-cols-2"}`}>
      {images.map((src, i) => (
        <a key={src} href={full(src)} target="_blank" rel="noreferrer" className={`${one ? "max-h-[560px]" : "aspect-square"} ${images.length === 3 && i === 0 ? "row-span-2 aspect-auto" : ""} block overflow-hidden bg-stone-100`}>
          <img src={full(src)} alt="" loading="lazy" className="h-full w-full object-cover" />
        </a>
      ))}
    </div>
  );
};

const Comments = ({ post, onCount }) => {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [body, setBody] = useState("");
  useEffect(() => { api.get(`/posts/${post.id}/comments`).then((r) => setList(r.data)); }, [post.id]);
  const send = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/posts/${post.id}/comments`, { body }); setList([...(list || []), data]); setBody(""); onCount(1); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="post-comments" className="mt-4 space-y-4 border-t border-[#EAE4DC] pt-4">
      {(list || []).map((c) => (
        <div key={c.id} data-testid="post-comment">
          <AuthorLine a={c.author} at={c.created_at} small />
          <p className="ml-9 mt-1 whitespace-pre-line text-sm text-stone-700">{c.body}</p>
        </div>
      ))}
      {user ? (
        <form onSubmit={send} className="flex gap-2">
          <input data-testid="comment-input" required maxLength={1000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment…" className="h-10 flex-1 rounded-full border border-[#EAE4DC] px-4 text-sm outline-none focus:border-pink-400" />
          <button data-testid="comment-submit" className="rounded-full bg-stone-900 px-4 text-sm text-white">Send</button>
        </form>
      ) : <p className="text-xs text-stone-500"><Link to="/join" className="underline">Join with Instagram</Link> to comment.</p>}
    </div>
  );
};

export const PostCard = ({ post, onDeleted }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(post);
  const [open, setOpen] = useState(false);
  const like = async () => {
    if (!user) { toast("Join with Instagram to like posts"); return nav("/join"); }
    try { const { data } = await api.post(`/posts/${p.id}/like`); setP({ ...p, ...data }); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/posts/${p.id}`); toast.success("Post deleted"); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <article data-testid="feed-post" className="rise rounded-[2rem] border border-[#EAE4DC] bg-white p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <AuthorLine a={p.author} at={p.created_at} />
        {p.can_delete && <button data-testid="post-delete" onClick={del} className="text-stone-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
      </div>
      {p.body && <p data-testid="post-body" className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-stone-800">{p.body}</p>}
      <Images images={p.images} />
      <div className="mt-4 flex items-center gap-6 text-sm text-stone-600">
        <button data-testid="post-like" onClick={like} className={`inline-flex items-center gap-1.5 transition-transform active:scale-90 ${p.liked ? "text-rose-500" : "hover:text-rose-500"}`}>
          <Heart className={`h-5 w-5 ${p.liked ? "fill-rose-500" : ""}`} /><span data-testid="post-like-count">{p.likes}</span>
        </button>
        <button data-testid="post-comments-toggle" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 hover:text-pink-600">
          <MessageCircle className="h-5 w-5" /><span data-testid="post-comment-count">{p.comment_count}</span>
        </button>
      </div>
      {open && <Comments post={p} onCount={(n) => setP((x) => ({ ...x, comment_count: x.comment_count + n }))} />}
    </article>
  );
};
